import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm } from 'react-hook-form';
import { DateRange } from '@mui/lab/DateRangePicker';
import { Route, Switch, useHistory, useLocation } from 'react-router-dom';
import { differenceInDays, differenceInCalendarMonths, format, formatDistanceToNowStrict } from 'date-fns';
import { DateTime } from 'luxon';
import './Payment.scss';
import {
  formatAccountType,
  formatDateForCalculation,
  findViewValuesHelper,
  getSearchParamValue,
  handleServicePaymentActivateAnalytics,
  handleServicePaymentSuspensionAnalytics,
  isMoviesBoughtAnalytics,
  getErrorMessage,
} from '../../utils';
import {
  AccountTypes,
  GlobalRouteNames,
  LocalStorageItems,
  ModalVariants,
  QueryParams,
  SharesAvailableList,
  SuccessRouteNames,
} from '../../models/Common';
import { ServicesTypes } from '../../models/Accounts';
import { AdditionalOptions } from '../../components/servicePaymentParts/AdditionalOptions';

import {
  dateForChanging,
  defaultAccountView,
  monthNumbers,
  suspensionOfServicesIds,
  toDay,
  turboGigShareId,
  shareActionID,
} from '../../constants/configuration';
import { Page404 } from '../404';
import { ApiError } from '../../models/ApiError';
import { AccountView, OrderParams } from '../../models/Calculate';
import { paymentsApi } from '../../services/apiServices/user/payments';
import { userInfoApi } from '../../services/apiServices/user/userInfo';
import { useAccountInfo } from '../../hooks/useAccountInfo';
import DateVO from '../../core/valueObjects/DateVO';
import { useCalculate } from '../../hooks/useCalculate';
import { useUserAddress } from '../../hooks/useUserAddress';
import { usePaymentAdditionalServices } from '../../hooks/usePaymentAdditionalServices';
import { useCustomSnackbar } from '../../hooks/useCustomEnqueueSnackbar';
import { useMonthToggles } from '../../hooks/useMonthToggles';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { useDateFsnLocale } from '../../hooks/useDateFsnLocale';
import { InfoBlock } from '../../components/servicePaymentParts/InfoBlock';
import { useDebounce } from '../../hooks/useDebounce';
import { useDateLocalization } from '../../hooks/useDateLocalization';
import { SharePromoBRIZTVPremium } from '../../components/servicePaymentParts/SharePromoBRIZTVPremium';
import { CustomerShare, ShareNames } from '../../models/ServicePresets';
import { modalSlice } from '../../store/reducers/ModalSlice';
import { initialState } from '../../store/reducers/UserInfoSlice';
import { UserSubtitle } from '../../components/commons/UserSubtitle';
import { ServicePaymentFormOptions } from '../../components/servicePaymentParts/ServicePaymentForm/ServicePaymentFormOptions';
import { ServicePaymentFormSubmission } from '../../components/servicePaymentParts/ServicePaymentForm/ServicePaymentFormSubmission';
import { useCustomerShareParams } from '../../hooks/useCustomerShareParams';
import { DateVOv2 } from '../../core/DateVOv2';
import BonusPointsCheckbox from '../../components/servicePaymentParts/BonusPointsCheckbox/BonusPointsCheckbox';

export interface ChangeCurrentServiceProps {
  isFilmsChanged: boolean;
  isIpChanged: boolean;
}

export interface ServicePaymentFormState {
  activationDate: DateTime;
  numberOfMonth: number;
  dedicatedIp?: boolean;
  films?: boolean;
  activationPeriod: DateRange<DateTime>;
}

interface ServiceInfoChange {
  newServiceIsDaily: boolean;
  serviceIsPrepaid: boolean;
}

const initialParamsForChangingNow: ChangeCurrentServiceProps = {
  isFilmsChanged: false,
  isIpChanged: false,
};

export const Payment: FC = () => {
  const { t } = useTranslation();
  const { selectedLanguage } = useAppSelector((state) => state.langReducer);
  const { fnsLocale } = useDateFsnLocale(selectedLanguage);
  const { search, pathname } = useLocation();
  const { formatDate } = useDateLocalization();
  const { openModal } = modalSlice.actions;
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { customEnqueueSnackbar } = useCustomSnackbar();
  const controlledAccount = localStorage.getItem(LocalStorageItems.ControlledAccount);
  const { userInfoState: adminInfoState } = useAppSelector((state) => state.userInfoReducer);
  const { useGetUserInfoQuery, useGetUserInfoMultiAccQuery } = userInfoApi;
  const { data: userInfoState } = controlledAccount
    ? useGetUserInfoMultiAccQuery({ subAccountId: JSON.parse(controlledAccount).UserID })
    : useGetUserInfoQuery('');
  const [serviceActivateAPI] = paymentsApi.useServicePaymentMutation();
  const [isLoading, setIsLoading] = useState(false);
  const [alertState, setAlertState] = useState({
    isFilms: false,
    isBonus: false,
    isDedicatedIp: false,
    isDiscountAvailable: false,
  });
  const { userAddress } = useUserAddress(userInfoState || initialState.userInfoState, selectedLanguage);
  const [availableMonthsCountShare50Percent, setAvailableMonthsCountShare50Percent] = useState(0);
  const accountType = formatAccountType(getSearchParamValue(QueryParams.AccountType, search));
  const accountId = getSearchParamValue(QueryParams.AccountId, search);
  const {
    data: servicePresets,
    isLoading: isLoadingServicePreset,
    error: servicePresetError,
  } = paymentsApi.useGetServicePresetsQuery({ accountId, accountType }, { skip: !accountType });
  const customerShare: CustomerShare | undefined = servicePresets?.CustomerShares[0];
  const currentService = servicePresets?.CurrentService;
  const currentServiceId = currentService?.id;
  const currentServiceTypeId = currentService?.Type?.id;
  const isCurrentServiceOfInternetDailyType = currentServiceTypeId === ServicesTypes.INTERNET_DAILY;
  const isCurrentServiceDaily = currentService?.Daily;
  const currentServiceInternetID = currentService && (currentService.InetServiceID || currentService.id);
  const canChangeService = servicePresets?.CanChangeCurrentService || false;
  const isActivated = servicePresets?.StatusID === 1 || (servicePresets && !!servicePresets.Prepaids) || false;
  /* isServiceActiveToday необходим только при моментальной смене для проверки, что сервис активен в текущем месяце,
   * так как isActivated возвращает "true" если текущий месяц(дата) не оплачен, но есть предоплаты в дальнейшем
   * */
  const isServiceActiveToday = servicePresets?.StatusID === 1 || false;
  const toChangeServiceId = +getSearchParamValue(QueryParams.ServiceId, search) || undefined;
  const toChangeService = servicePresets?.Services?.find((el) => el.id === toChangeServiceId);
  const futureServiceId = toChangeService ? toChangeService?.id : currentServiceId;
  const toChangeServiceTypeId = toChangeService?.Type?.id;
  const isFutureServiceOfInternetDailyType = toChangeServiceTypeId === ServicesTypes.INTERNET_DAILY;
  const isFutureServiceDaily = toChangeService?.Daily;
  const isServiceDailyUnavailable = !isCurrentServiceDaily && !isFutureServiceDaily;
  const isDaily = isFutureServiceDaily ?? isCurrentServiceDaily ?? false;
  const isPrepaidServiceDefault =
    (isCurrentServiceOfInternetDailyType || isCurrentServiceDaily) &&
    (!isFutureServiceOfInternetDailyType || !isFutureServiceDaily);
  // const monthToActivate = servicePresets ? DateVO.create(servicePresets.FromDate).get() : toDay;
  const monthToActivate = servicePresets
    ? new DateVOv2({ dateString: servicePresets.FromDate, format: 'yyyy-MM-dd HH:mm:ss' }).getLuxonKiev()
    : new DateVOv2().getLuxonKiev();
  // console.log('monthToActivate', monthToActivate);
  // console.log('monthToActivate2', monthToActivate2);
  const monthlyCost = toChangeService?.Price || currentService?.Price;
  const isCurrentServiceSuspension = suspensionOfServicesIds.some((suspensionId) => suspensionId === currentServiceId);
  const isFutureServiceSuspension = suspensionOfServicesIds.some((suspensionId) => suspensionId === toChangeServiceId);
  const isSuspension = toChangeService ? isFutureServiceSuspension : isCurrentServiceSuspension;
  const isCableDisconnected = servicePresets?.IsDisabled ?? false;
  const isInfoBlockOneMonth = isServiceActiveToday || isDaily || accountType === AccountTypes.Ctv;
  const isOneMonth =
    isActivated ||
    isDaily ||
    isCableDisconnected ||
    currentServiceTypeId === ServicesTypes.INTERNET_TV ||
    currentServiceTypeId === ServicesTypes.INTERNET_TV_MAX ||
    accountType === AccountTypes.Ctv;
  const isServiceActivationAvailable = userInfoState?.Permissions.Services.Activate;
  const fastConnectStaticIP = getSearchParamValue(QueryParams.ConnectStaticIp, search);
  const fastConnectFilms = getSearchParamValue(QueryParams.ConnectFilms, search);
  const serviceName = toChangeService?.Name || currentService?.Name || '';
  const serviceAnalyticsName = toChangeService?.AnalyticsID ?? currentService?.AnalyticsID ?? serviceName;
  const payedService = toChangeService || currentService;
  const shareAction = payedService?.Share ?? false;
  const activatedTurboGig = userInfoState?.Shares?.Activated.find((item) => item.id === turboGigShareId);
  const availableInstantChanges = servicePresets?.ChangeCurrentServiceInfo
    ? servicePresets?.ChangeCurrentServiceInfo.max_count - servicePresets?.ChangeCurrentServiceInfo.current_count
    : undefined;

  const isAvailableConnect6MonthFor50Percents =
    servicePresets && ShareNames.Connect6MonthFor50Percents in servicePresets.Shares;
  const isAvailableShare6Month50Percent = !!(
    isAvailableConnect6MonthFor50Percents &&
    servicePresets.Shares[ShareNames.Connect6MonthFor50Percents].availiable_inet_services.includes(
      toChangeServiceId || currentServiceInternetID || 0,
    )
  );
  const isAvailableShare6Month50PercentInet = !!(
    isAvailableConnect6MonthFor50Percents &&
    !servicePresets.Shares[ShareNames.Connect6MonthFor50Percents].notavailiable_inet_services.includes(
      futureServiceId || 0,
    )
  );
  const isAvailableShare6Month50PercentCtv = !!(
    isAvailableConnect6MonthFor50Percents &&
    !servicePresets.Shares[ShareNames.Connect6MonthFor50Percents].notavailiable_ctv_services.includes(
      futureServiceId || 0,
    )
  );
  const isAvailableShare6Month50PercentIptv = !!(
    isAvailableConnect6MonthFor50Percents &&
    !servicePresets.Shares[ShareNames.Connect6MonthFor50Percents].notavailiable_iptv_services.includes(
      futureServiceId || 0,
    )
  );
  const isAvailableShare6Month50PercentUnion = !!(
    isAvailableConnect6MonthFor50Percents &&
    !servicePresets.Shares[ShareNames.Connect6MonthFor50Percents].notavailiable_union_services.includes(
      futureServiceId || 0,
    )
  );
  const availableDaysCountShare50Percent =
    (isAvailableConnect6MonthFor50Percents &&
      servicePresets.Shares[ShareNames.Connect6MonthFor50Percents].availableDaysCount) ||
    0;

  const serviceInfo: ServiceInfoChange = {
    newServiceIsDaily: isFutureServiceDaily || false,
    serviceIsPrepaid: !!servicePresets?.Prepaids && (servicePresets?.Prepaids.length > 0 || false),
  };

  const [paramsForChangingNow, setParamsForChangingNow] =
    useState<ChangeCurrentServiceProps>(initialParamsForChangingNow);

  const [paramsForCalculation, setParamsForCalculation] = useState<OrderParams>({
    accountId,
    accountType,
    ServiceExIDs: [],
  });

  const { accountInfo } = useAccountInfo(accountId, accountType);
  const { films, staticIp, isHandling } = usePaymentAdditionalServices(
    isDaily,
    accountType,
    isLoadingServicePreset,
    toChangeService,
    servicePresets,
  );

  const getMonthTogglesDeps = (id: number): [ChangeCurrentServiceProps, number?, number?] => {
    const currentId = id || undefined;
    let params = initialParamsForChangingNow;
    let futureId;

    if (currentId) {
      params = paramsForChangingNow;
      futureId = futureServiceId;
    }
    return [params, currentId, futureId];
  };
  // const initialActivationDate = isDaily && isBefore(monthToActivate, toDay) ? toDay : monthToActivate;
  const initialActivationDate =
    isDaily && monthToActivate.startOf('day') < new DateVOv2().getLuxonKiev().startOf('day')
      ? new DateVOv2().getLuxonKiev()
      : monthToActivate;
  // console.log('initialActivationDate', initialActivationDate);
  // console.log('initialActivationDate2', initialActivationDate2);
  const { serviceIsPrepaid } = serviceInfo;
  const isIdEquals = currentService?.id === futureServiceId;
  const isChangesInService = !isIdEquals || paramsForChangingNow.isIpChanged || paramsForChangingNow.isFilmsChanged;
  const isChangesInServiceNow = isChangesInService && (!isFutureServiceOfInternetDailyType || !isDaily);
  const isChangesInServiceDaily = isChangesInService && (isFutureServiceOfInternetDailyType || isDaily);
  const isChangesInServiceNoEdit = !canChangeService && isChangesInService;
  const isExtraServiceAdded =
    isIdEquals &&
    ((!staticIp.isPreviouslyBuy && paramsForChangingNow.isIpChanged) ||
      (!films.isPreviouslyBuy && paramsForChangingNow.isFilmsChanged));
  const isExtraServiceRemoved =
    isIdEquals &&
    ((staticIp.isPreviouslyBuy && paramsForChangingNow.isIpChanged) ||
      (films.isPreviouslyBuy && paramsForChangingNow.isFilmsChanged));
  const canChangeServiceExtra = canChangeService || isExtraServiceAdded || isExtraServiceRemoved;
  const isExtraServiceRemove = !isHandling && isExtraServiceRemoved;
  const isExtraServiceAdd = !isHandling && isExtraServiceAdded;
  const isNotPrepaidServiceDaily = isFutureServiceOfInternetDailyType || serviceInfo.newServiceIsDaily;

  const { monthToggles, initialActivationFromDate, instantPackageChange } = useMonthToggles({
    isServiceActiveToday,
    isServiceDailyUnavailable,
    initialActivationDate,
    canChangeServiceExtra,
    isExtraServiceRemove,
    isExtraServiceAdd,
    locale: fnsLocale?.code,
    serviceIsPrepaid,
    isChangesInServiceNow,
    isChangesInServiceDaily,
    isChangesInServiceNoEdit,
    isInfoBlockOneMonth,
    isOneMonth,
    isCurrentServiceDaily,
    isPrepaidServiceDefault,
    isNotPrepaidServiceDaily,
    deps: getMonthTogglesDeps(accountInfo.serviceId),
  });

  /* ====== формирование необходимых параметров для активации акции "3 месяца -30%" ===== */
  const customerShareParams = useCustomerShareParams(customerShare, accountId, accountType, fnsLocale, servicePresets);
  /* ==================================================================== */

  useEffect(() => {
    if (!isLoadingServicePreset && servicePresetError) {
      const err = servicePresetError as ApiError;
      if (err.status === 403) {
        customEnqueueSnackbar(err?.data?.error?.message, 'error');
      } else {
        const message = getErrorMessage(servicePresetError);
        customEnqueueSnackbar(message, 'error');
      }
    }
  }, [servicePresetError]);

  const updateParamsForCalculation = () => {
    setParamsForCalculation({
      ...paramsForCalculation,
      dateFrom: instantPackageChange.isDisabled ? monthToggles[1].value : monthToggles[0].value,
      changeCurrentService: instantPackageChange.isAllowChangeService,
      serviceId: toChangeServiceId || currentServiceId,
    });
  };

  useEffect(
    () => {
      if (isDaily && !isLoadingServicePreset) {
        updateParamsForCalculation();
      }
      if (!isDaily) {
        updateParamsForCalculation();
      }
    },
    isDaily ? [monthToggles.length, isLoadingServicePreset] : [servicePresets, monthToggles.length],
  );

  const {
    isLoading: isCalculateLoading,
    isLoaded: isCalculateLoaded,
    isInitialDiscount50Percent,
    currentCalculation,
  } = useCalculate(paramsForCalculation, !servicePresets);
  const isCtv = accountType === AccountTypes.Ctv;
  const accountView: AccountView =
    currentCalculation.view?.hybrid?.[0] ??
    currentCalculation.view?.ctv?.[0] ??
    currentCalculation.view?.iptv?.[0] ??
    currentCalculation.view?.inet?.[0] ??
    defaultAccountView;

  const daysRemain = formatDistanceToNowStrict(DateVO.create(currentCalculation.toDate).get(), {
    unit: 'day',
    locale: fnsLocale,
    roundingMethod: 'ceil',
  });
  const untilDate = formatDate(currentCalculation.toDate, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    locale: 'uk',
  });
  const dayDifference =
    differenceInDays(DateVO.create(currentCalculation.toDate).get(), DateVO.create(currentCalculation.fromDate).get()) +
    1;

  const searchParams = new URLSearchParams(search);
  const activationPeriod = t(
    'paymentHistory.paymentPeriod',
    isDaily
      ? {
          from: searchParams.get('dateFrom'),
          to: searchParams.get('dateTo'),
        }
      : {
          from: currentCalculation.fromDate && format(DateVO.create(currentCalculation.fromDate).get(), 'dd.MM.yyyy'),
          to: currentCalculation.fromDate && format(DateVO.create(currentCalculation.toDate).get(), 'dd.MM.yyyy'),
        },
  );

  const methods = useForm<ServicePaymentFormState>({
    // defaultValues: {
    //   activationDate: isActivated
    //     ? initialActivationFromDate.toFormat('dd-MM-yyyy')
    //     : new DateVOv2().getLuxonKiev().toFormat('dd-MM-yyyy'),
    //   numberOfMonth: 1,
    //   activationPeriod: [
    //     initialActivationFromDate.toFormat('dd-MM-yyyy'),
    //     initialActivationFromDate.toFormat('dd-MM-yyyy'),
    //   ],
    // },
    defaultValues: {
      activationDate: isActivated ? initialActivationFromDate : new DateVOv2().getLuxonKiev(),
      numberOfMonth: 1,
      activationPeriod: [initialActivationFromDate, initialActivationFromDate],
    },
  });

  useEffect(() => {
    const searchParamsURL = new URLSearchParams(search);

    if (isDaily && !isCalculateLoading) {
      const to = format(DateVO.create(currentCalculation.toDate).get(), 'dd.MM.yyyy');
      const from = format(DateVO.create(currentCalculation.fromDate).get(), 'dd.MM.yyyy');
      searchParamsURL.set('dateFrom', from);
      searchParamsURL.set('dateTo', to);
    }

    const newURL = `${pathname}?${searchParamsURL.toString()}`;
    if (isDaily) {
      history.replace(newURL);
    }
  }, [currentCalculation, isCalculateLoading]);

  const onChangeFilms = (e: boolean) => {
    setParamsForCalculation((prevState) => {
      const isIptvAccount = prevState && accountType === AccountTypes.Iptv;
      const isUnionAccount = prevState && accountType === AccountTypes.Union;

      setAlertState({ ...alertState, isFilms: e });

      if (isIptvAccount && Array.isArray(prevState.ServiceExIDs)) {
        if (e) {
          return {
            ...prevState,
            serviceExIds: [films.id],
          };
        }
        return {
          ...prevState,
          serviceExIds: Array.isArray(prevState.ServiceExIDs)
            ? prevState.ServiceExIDs?.filter((el) => el !== films.id)
            : [],
        };
      }

      if (isUnionAccount && typeof prevState.ServiceExIDs === 'object') {
        if (e) {
          return {
            ...prevState,
            serviceExIds: { ...prevState.ServiceExIDs, iptv: [films.id] },
          };
        }
        const updatedServiceExIds = { ...prevState.ServiceExIDs, iptv: [] };
        return {
          ...prevState,
          serviceExIds: updatedServiceExIds,
        };
      }

      return prevState;
    });
  };

  const onChangeDedicatedIp = (e: boolean) => {
    setParamsForCalculation((prevState) => {
      const isInternetAccount = prevState && accountType === AccountTypes.Internet;
      const isUnionAccount = prevState && accountType === AccountTypes.Union;

      setAlertState({ ...alertState, isDedicatedIp: e });

      if (isInternetAccount && Array.isArray(prevState.ServiceExIDs)) {
        if (e) {
          return {
            ...prevState,
            serviceExIds: [staticIp.id],
          };
        }
        return {
          ...prevState,
          serviceExIds: Array.isArray(prevState.ServiceExIDs)
            ? prevState.ServiceExIDs?.filter((el) => el !== staticIp.id)
            : [],
        };
      }

      if (isUnionAccount && typeof prevState.ServiceExIDs === 'object') {
        if (e) {
          return {
            ...prevState,
            serviceExIds: { ...prevState.ServiceExIDs, inet: [staticIp.id] },
          };
        }
        const updatedServiceExIds = { ...prevState.ServiceExIDs, inet: [] };
        return {
          ...prevState,
          serviceExIds: updatedServiceExIds,
        };
      }

      return prevState;
    });
  };

  const onChangeUseBonusPoints = (e: boolean) => {
    setAlertState({ ...alertState, isBonus: e });
    setParamsForCalculation({ ...paramsForCalculation, isBonusPayment: e });
  };

  const handleChangeFilms = (e: boolean) => {
    onChangeFilms(e);
    setParamsForChangingNow((prevState) => ({ ...prevState, isFilmsChanged: !prevState.isFilmsChanged }));
  };

  const handleChangeDedicatedIp = (e: boolean) => {
    onChangeDedicatedIp(e);
    setParamsForChangingNow((prevState) => ({ ...prevState, isIpChanged: !prevState.isIpChanged }));
  };

  const handleActivate = async () => {
    setIsLoading(true);
    try {
      await serviceActivateAPI(paramsForCalculation)
        .unwrap()
        .then(() => {
          localStorage.setItem(LocalStorageItems.ServiceName, serviceName);
          localStorage.setItem(LocalStorageItems.FromDate, currentCalculation.fromDate);
          localStorage.setItem(LocalStorageItems.ToDate, currentCalculation.toDate);
          if (isSuspension) {
            handleServicePaymentSuspensionAnalytics(
              currentService?.AnalyticsID ?? serviceName,
              currentCalculation.monthCount,
              currentCalculation.amount,
            );
            history.push(`${GlobalRouteNames.SuccessPage}${SuccessRouteNames.ServiceSuspendedSuccessfulPage}`);
          } else {
            handleServicePaymentActivateAnalytics(
              serviceAnalyticsName,
              currentCalculation.monthCount,
              currentCalculation.amount,
              isMoviesBoughtAnalytics(paramsForCalculation.ServiceExIDs, films.isFilms),
            );
            history.push(`${GlobalRouteNames.SuccessPage}${SuccessRouteNames.ServiceActivationSuccessfulPage}`);
          }
        });
    } catch (err) {
      const message = getErrorMessage(err);
      customEnqueueSnackbar(message, 'error');
    }
  };

  const handlePay = () => {
    // dispatch(
    //   openModal({
    //     modalVariants: ModalVariants.PaymentTypeMethods,
    //     content: { service: serviceName, currentCalculation, paramsForCalculation },
    //   }),
    // );
  };

  const handleChangeCountOfMonth = (count: number) => {
    setParamsForCalculation({ ...paramsForCalculation, count });
    setAlertState({ ...alertState, isDiscountAvailable: count > 2 });
  };

  const handleSliderChange = useDebounce((number: number) => {
    handleChangeCountOfMonth(+number);
    methods.setValue('numberOfMonth', number);
  }, 200);

  const isServiceInPromo = () => {
    switch (accountType) {
      case AccountTypes.Internet:
        return isAvailableShare6Month50PercentInet;
      case AccountTypes.Ctv:
        return isAvailableShare6Month50PercentCtv;
      case AccountTypes.Iptv:
        return isAvailableShare6Month50PercentIptv;
      case AccountTypes.Union:
        return isAvailableShare6Month50PercentUnion;
      default:
        return false;
    }
  };

  const updateAvailable50PercentMonths = (isSuspensionTo50Percents: boolean, date: string) => {
    if (isAvailableConnect6MonthFor50Percents) {
      const expireDate = userInfoState?.Shares?.Activated.find(
        (item) => item.id === servicePresets.Shares[ShareNames.Connect6MonthFor50Percents].model.id,
      )?.ActiveTo;
      if (isSuspensionTo50Percents) {
        const availableMonths = differenceInCalendarMonths(DateVO.create(String(expireDate)).get(), toDay) + 1;
        setAvailableMonthsCountShare50Percent(availableMonths);
      } else {
        const availableMonths =
          date && expireDate && 'availableMonthsCount' in servicePresets.Shares[ShareNames.Connect6MonthFor50Percents]
            ? differenceInCalendarMonths(DateVO.create(String(expireDate)).get(), DateVO.create(String(date)).get()) + 1
            : servicePresets.Shares[ShareNames.Connect6MonthFor50Percents].availableMonthsCount || 0;
        setAvailableMonthsCountShare50Percent(availableMonths >= 0 && isServiceInPromo() ? availableMonths : 0);
      }
    }
  };

  const handleActivationMonthChange = (date: string) => {
    if (date) {
      const isToday = new DateVOv2({ dateString: date, format: 'dd-MM-yyyy' })
        .getLuxonKiev()
        .hasSame(new DateVOv2().getLuxonKiev(), 'day');
      const isSuspensionTo50Percents =
        isToday && isActivated && isCurrentServiceSuspension && isInitialDiscount50Percent;

      updateAvailable50PercentMonths(
        isSuspensionTo50Percents,
        new DateVOv2({ dateString: date, format: 'dd-MM-yyyy' }).getLuxonKiev().toJSDate().toDateString(),
      );

      setParamsForCalculation({
        ...paramsForCalculation,
        dateFrom: isActivated && isToday ? new DateVOv2().getLuxonKiev().toFormat('dd-MM-yyyy') : date,
        changeCurrentService: isActivated && isToday,
      });
    }
  };

  const handleChangeDateRange = (dates: DateRange<DateTime>) => {
    if (dates[0] && dates[1]) {
      setParamsForCalculation({
        ...paramsForCalculation,
        dateFrom: dates[0].toFormat('dd-MM-yyyy'),
        count: dates[1].diff(dates[0], 'days').days + 1,
      });
    }
    if (dates[0] && !dates[1]) {
      setParamsForCalculation({
        ...paramsForCalculation,
        dateFrom: dates[0].toFormat('dd-MM-yyyy'),
        count: 1,
      });
    }
  };

  useEffect(
    () => {
      if (isDaily) {
        const searchParamsUrl = new URLSearchParams(search);
        const dateFrom = searchParamsUrl.get('dateFrom');
        const dateTo = searchParamsUrl.get('dateTo');
        if (dateFrom && dateTo) {
          methods.setValue(
            'activationDate',
            new DateVOv2({ dateString: dateFrom, format: 'dd.MM.yyyy' }).getLuxonKiev(),
          );
          methods.setValue('activationPeriod', [
            new DateVOv2({ dateString: dateFrom, format: 'dd.MM.yyyy' }).getLuxonKiev(),
            new DateVOv2({ dateString: dateTo, format: 'dd.MM.yyyy' }).getLuxonKiev(),
          ]);
        }
      } else {
        methods.setValue('activationDate', initialActivationFromDate);
        methods.setValue('activationPeriod', [initialActivationFromDate, initialActivationFromDate]);
      }
    },
    isDaily ? [search] : [servicePresets, accountInfo, monthToggles.length],
  );

  useEffect(() => {
    if (!isHandling && films.isFilms) {
      if (fastConnectFilms && !paramsForChangingNow.isFilmsChanged) {
        handleChangeFilms(true);
      }
      if (films.isPreviouslyBuy && !paramsForChangingNow.isFilmsChanged) {
        onChangeFilms(true);
      }
    }
  }, [films]);

  useEffect(() => {
    if (!isHandling && staticIp.isStaticIp) {
      if (fastConnectStaticIP && !paramsForChangingNow.isIpChanged) {
        handleChangeDedicatedIp(true);
      }
      if (staticIp.isPreviouslyBuy && !paramsForChangingNow.isIpChanged) {
        onChangeDedicatedIp(true);
      }
    }
  }, [staticIp]);

  useEffect(() => {
    if (
      availableMonthsCountShare50Percent &&
      !isSuspension &&
      currentCalculation.monthCount > availableMonthsCountShare50Percent
    ) {
      handleSliderChange(availableMonthsCountShare50Percent);
    }
  }, [availableMonthsCountShare50Percent]);

  useEffect(() => {
    if (isAvailableConnect6MonthFor50Percents && isCalculateLoaded) {
      const isToday = format(new Date(currentCalculation.fromDate), 'dd.MM.yyyy') === format(toDay, 'dd.MM.yyyy');
      const isSuspensionTo50Percents = isToday && !isDaily && isCurrentServiceSuspension && isInitialDiscount50Percent;

      updateAvailable50PercentMonths(isSuspensionTo50Percents, currentCalculation.fromDate);
    }
  }, [isAvailableConnect6MonthFor50Percents, isCalculateLoaded]);

  const userBonuses = adminInfoState.Bonus;
  const userBalance = adminInfoState.Balance;
  const isMonthly = toChangeService?.Monthly ?? currentService?.Monthly ?? false;
  const isNeedToPay = !!currentCalculation.payAmount;
  let isBonusPaymentAllowed = toChangeService?.IsBonusPaymentAllowed ?? currentService?.IsBonusPaymentAllowed ?? false;
  if (!isBonusPaymentAllowed && (alertState.isDedicatedIp || alertState.isFilms)) {
    isBonusPaymentAllowed = true;
  }
  const isPaymentByBonusAvailable =
    userInfoState?.Permissions?.Info?.ShowBonuses && isBonusPaymentAllowed && !isCtv && userBonuses > 0;

  const isDateActivation =
    (paramsForCalculation?.dateFrom &&
      paramsForCalculation?.dateFrom === formatDateForCalculation(DateVO.create(toDay.toString()).get())) ||
    null;

  if (isDaily) {
    toDay.setHours(0, 0, 0, 0);
  }

  const dateFromForm =
    typeof methods.watch('activationDate') === 'string'
      ? new DateVOv2({
          format: 'dd-MM-yyyy',
          dateString: methods.watch('activationDate') as unknown as string,
        }).getLuxonKiev()
      : methods.watch('activationDate');

  const isDisabledPaymentButtonInstantChange =
    dateFromForm.day === new DateVOv2().getLuxonKiev().day &&
    dateFromForm.month === new DateVOv2().getLuxonKiev().month &&
    dateFromForm.year === new DateVOv2().getLuxonKiev().year &&
    !instantPackageChange.isAllowChangeService &&
    !isDaily &&
    isServiceActiveToday;

  const isMonthRangeVisible = !isDaily && (instantPackageChange.isMonthVisible || !isDateActivation);
  const isMonthDiscount50Available = currentCalculation.discountPercent === 50;
  const availableMonthsCountShare50PercentExists = availableMonthsCountShare50Percent > 0;
  const isAvailableMonthRangeExceedPromo =
    availableMonthsCountShare50PercentExists &&
    (!isMonthDiscount50Available || (isMonthDiscount50Available && availableMonthsCountShare50Percent <= 11)) &&
    !isSuspension &&
    isCalculateLoaded;
  const isAvailable50PercentDateRangePromo = isAvailableShare6Month50Percent && availableDaysCountShare50Percent > 0;
  const isAvailableDateRangeExceedPromo =
    isAvailable50PercentDateRangePromo && availableDaysCountShare50Percent < accountView.num;
  const isVisibleBillContent =
    (!isDisabledPaymentButtonInstantChange || instantPackageChange.isMonthVisible) &&
    !(
      instantPackageChange.isInfoBlock &&
      (instantPackageChange.nameCondition === 'notPrepaid' || instantPackageChange.nameCondition === 'default') &&
      isDateActivation
    );
  const isPayAmountNull =
    !isDaily &&
    instantPackageChange.isInfoBlock &&
    ((instantPackageChange.nameCondition === 'prepaid' && isDateActivation) ||
      ((instantPackageChange.nameCondition === 'notPrepaid' || instantPackageChange.nameCondition === 'default') &&
        isDateActivation));
  const isShownPresets = !availableMonthsCountShare50PercentExists && !isSuspension && isCalculateLoaded;
  const isVisibleTotalBalance =
    instantPackageChange.isInfoBlock && instantPackageChange.nameCondition === 'now' && isDateActivation;
  const billDiscountPercent =
    currentCalculation.discountAmount > 0 && isSuspension && !currentCalculation.discountPercent
      ? findViewValuesHelper(currentCalculation.view, 'ex_amo')[0].dsc_percent
      : currentCalculation.discountPercent;
  const proceedLabel = isNeedToPay ? t('buttons.pay') : t('buttons.activate');
  const internetLogin =
    accountType === AccountTypes.Internet || accountType === AccountTypes.Union ? accountInfo.internetLogin : null;

  useEffect(() => {
    if (isVisibleTotalBalance && isPaymentByBonusAvailable && alertState.isBonus) {
      onChangeUseBonusPoints(false);
    }
  }, [isVisibleTotalBalance]);

  const handleRestore = () => {
    dispatch(
      openModal({
        modalVariants: ModalVariants.RestoreCallback,
        content: {
          service: serviceName,
          accountType,
        },
      }),
    );
  };

  return (
    <Switch>
      <Route path={GlobalRouteNames.ServicePayment} exact>
        <div className="page payment">
          <div className="payment__container container">
            {servicePresets?.CustomerShares.some((el) => el.id === SharesAvailableList.SHARE_3_MONTHS) &&
            customerShare ? (
              <SharePromoBRIZTVPremium customerShareParams={customerShareParams} />
            ) : null}
            <div className="payment__card card card__primary card__primary_full-mobile">
              {!controlledAccount ? (
                <h1 className="payment__title">{t('servicePayment.serviceActivation')}</h1>
              ) : (
                <h1 className="payment__title">
                  {t('servicePayment.serviceAccountActivation')}
                  <UserSubtitle
                    address={userAddress.trim()}
                    userId={userInfoState?.id ?? initialState.userInfoState.id}
                  />
                </h1>
              )}
              <div className="payment__content">
                <FormProvider {...methods}>
                  <ServicePaymentFormOptions
                    accountView={accountView}
                    activationDates={monthToggles}
                    availableNumberOFMonths={monthNumbers}
                    discountPercent={billDiscountPercent}
                    earnBonusPercent={currentCalculation.earnBonusPercent}
                    handleActivationPeriodChange={handleActivationMonthChange}
                    handleChangeCountOfMonth={handleChangeCountOfMonth}
                    handleChangeDateRange={handleChangeDateRange}
                    initialActivationFromDate={initialActivationFromDate}
                    internetLogin={internetLogin}
                    isDaily={isDaily}
                    isMonthly={isMonthly}
                    isShownPresets={isShownPresets}
                    isCalculateLoading={isCalculateLoading}
                    isDiscountDisabled={!userInfoState?.Permissions?.Payments?.PrepayDiscount}
                    isBonusAccrualDisabled={!userInfoState?.Permissions?.Info?.ShowBonuses || isCtv}
                    monthCount={currentCalculation.monthCount}
                    monthlyCost={monthlyCost}
                    serviceName={serviceName}
                    films={
                      films.isFilms ? (
                        <AdditionalOptions
                          defaultChecked={
                            films.isPreviouslyBuy ||
                            !!fastConnectFilms ||
                            (shareAction && shareAction.id === shareActionID)
                          }
                          isDisabled={shareAction && shareAction.id === shareActionID}
                          onChangeAdditionalService={handleChangeFilms}
                          registerFormName="films"
                          label={t('servicePayment.filmsPrice', { val: films.price })}
                        />
                      ) : null
                    }
                    infoBlock={
                      instantPackageChange.isInfoBlock ? (
                        <InfoBlock
                          daysRemain={daysRemain}
                          untilDate={untilDate}
                          moreThanHalfMonth={!!shareAction || dayDifference > +dateForChanging}
                          isMonthCountChanged={!!paramsForCalculation.count && paramsForCalculation.count > 1}
                          handleChangeCountMonth={handleSliderChange}
                          state={instantPackageChange}
                          isDateActivation={isDateActivation}
                          availableInstantChanges={availableInstantChanges}
                        />
                      ) : null
                    }
                    staticIp={
                      staticIp.isStaticIp ? (
                        <AdditionalOptions
                          defaultChecked={staticIp.isPreviouslyBuy || !!fastConnectStaticIP}
                          onChangeAdditionalService={handleChangeDedicatedIp}
                          registerFormName="dedicatedIp"
                          label={t('servicePayment.staticIpPrice', { val: staticIp.price })}
                        />
                      ) : null
                    }
                    shareAction={
                      shareAction ? (
                        <>
                          &nbsp;&nbsp;&nbsp;
                          <span className="extra-prompt">
                            &quot;{shareAction.Name}&quot;{' '}
                            {shareAction.StopDate ? t('serviceManagement.nextPaymentDate').toLowerCase() : ''}{' '}
                            {shareAction.StopDate
                              ? formatDate(
                                  shareAction.id === turboGigShareId && !!activatedTurboGig
                                    ? activatedTurboGig.ActiveTo
                                    : shareAction.StopDate,
                                  {
                                    day: '2-digit',
                                    month: '2-digit',
                                    year: 'numeric',
                                    locale: 'uk',
                                  },
                                )
                              : ''}
                          </span>
                        </>
                      ) : null
                    }
                    availableMonthsCountShare50Percent={availableMonthsCountShare50Percent}
                    availableMonthsCountShare50PercentExists={availableMonthsCountShare50PercentExists}
                    isAvailableMonthRangeExceedPromo={isAvailableMonthRangeExceedPromo}
                    isAvailableDateRangeExceedPromo={isAvailableDateRangeExceedPromo}
                    isSuspension={isSuspension}
                    isMonthRangeVisible={isMonthRangeVisible}
                    isMonthDiscount50Available={isMonthDiscount50Available}
                  />
                  <ServicePaymentFormSubmission
                    monthCount={currentCalculation.monthCount}
                    discountAmount={currentCalculation.discountAmount}
                    payAmount={currentCalculation.payAmount}
                    isPayAmountNull={isPayAmountNull}
                    canBalanceAmount={currentCalculation.canBalanceAmount}
                    isBonusAccrualDisabled={!userInfoState?.Permissions?.Info?.ShowBonuses || isCtv}
                    earnBonusAmount={currentCalculation.earnBonusAmount}
                    serviceName={serviceName}
                    serviceAmount={accountView.service_amo}
                    proceedLabel={isCalculateLoaded ? proceedLabel : <>&nbsp;</>}
                    isLoading={isLoading || isCalculateLoading}
                    isActivationDisabled={!isServiceActivationAvailable || isDisabledPaymentButtonInstantChange}
                    isVisibleBillContent={isVisibleBillContent}
                    isVisibleTotalBalance={isVisibleTotalBalance}
                    userBonuses={userBonuses}
                    userBalance={userBalance}
                    handleProceed={isNeedToPay ? handlePay : handleActivate}
                    bonusPointsCheckbox={
                      isPaymentByBonusAvailable ? (
                        <BonusPointsCheckbox isBonus={alertState.isBonus} onChange={onChangeUseBonusPoints} />
                      ) : null
                    }
                    bonusesWithdrawn={currentCalculation.bonusAmount}
                    refund={{
                      backAmount: currentCalculation.backAmount,
                      backBonus: currentCalculation.backBonusAmount,
                    }}
                    isCableDisconnected={isCableDisconnected}
                    accountView={accountView}
                    handleRestore={handleRestore}
                    discountPercent={billDiscountPercent}
                    activationPeriod={activationPeriod}
                  />
                </FormProvider>
              </div>
            </div>
          </div>
        </div>
      </Route>
      <Route component={Page404} path={GlobalRouteNames.Page404} />
    </Switch>
  );
};
